import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { CacheContext } from "../contexts/CacheContext";

const categoryMap = {
  portraits: "portrait photography",
  nature: "nature landscape",
  weddings: "wedding photography",
  urban: "city street photography",
  animals: "wildlife animal",
  travel: "travel photography",
};

const AlbumPage = () => {
  const { albumName } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { cache, setCachedData } = useContext(CacheContext);

  // State to track which images are visible for animation
  const [visibleImageIds, setVisibleImageIds] = useState(new Set());
  const observer = useRef(null);

  // Callback to set up IntersectionObserver
  const setupObserver = useCallback(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleImageIds((prev) => new Set(prev).add(entry.target.dataset.imageId));
            observer.current.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: "0px",
        threshold: 0.1, // Trigger when 10% of the item is visible
      }
    );
  }, []);

  useEffect(() => {
    const cacheKey = `album-${albumName}`;
    const query = categoryMap[albumName];

    const fetchImages = async () => {
      if (cache[cacheKey]) {
        setImages(cache[cacheKey]);
        setLoading(false);
        return;
      }

      if (!query) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://api.unsplash.com/search/photos?query=${query}&per_page=20&client_id=xIVYxkrz6cl6LjI_THMykrl0yDcXP9UQWt6SdM2ydEM`
        );
        const data = await response.json();
        const imageData = data.results || [];
        setImages(imageData);
        setCachedData(cacheKey, imageData);
      } catch (error) {
        console.error("Error fetching album images:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [albumName, cache, setCachedData]);

  useEffect(() => {
    // Re-setup observer when images change
    setupObserver();
    // Observe all masonry items
    document.querySelectorAll(".masonry-item").forEach((item) => {
      observer.current.observe(item);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [images, setupObserver]); // Depend on images and setupObserver

  useEffect(() => {
    // Initialize Fancybox from the window object
    if (window.Fancybox) {
      window.Fancybox.bind("[data-fancybox]", {
        // Your custom options
      });
    }

    return () => {
      // Destroy Fancybox on component unmount
      if (window.Fancybox) {
        window.Fancybox.destroy();
      }
    };
  }, [images]); // Re-initialize Fancybox when images change

  if (loading) {
    return <Loader />;
  }

  if (images.length === 0) {
    return <div className="album-page">No images found for this album.</div>;
  }

  return (
    <div className="album-page">
      <h1 className="album-title">{albumName.charAt(0).toUpperCase() + albumName.slice(1)}</h1>
      <div className="image-masonry">
        {images.map((img) => (
          <a
            key={img.id}
            href={img.urls.regular}
            data-fancybox="gallery"
            data-caption={img.alt_description || ""}
            className={`masonry-item ${visibleImageIds.has(img.id) ? "is-visible" : ""}`}
            data-image-id={img.id} // Add data attribute for observer
          >
            <img
              src={img.urls.small}
              alt={img.alt_description || ""}
              className="masonry-image"
              loading="lazy"
            />
          </a>
        ))}
      </div>
    </div>
  );
};

export default AlbumPage;
